/**
 * WhatsApp Inbox — the WhatsApp two-way workspace (ADMIN + STAFF tracks).
 *
 * IA FIX 2026-05-21 (AVBP_DATA_MODEL_IA_SPEC_v1 Q3): the screen LABEL is
 * "WhatsApp Inbox" — renamed from "Messages" so it is never confused with
 * a project Conversation. The route (/messages), the component file name
 * and the data layer (messagesStore) are deliberately UNCHANGED — cosmetic
 * rename only, zero functional change.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Owner-approved scope: AVBP_WHATSAPP_WORKSPACE_SCOPE_2026-05-19.md
 *  Owner-approved look : AVBP_WHATSAPP_WORKSPACE_MOCKUP_2026-05-19.html
 * ─────────────────────────────────────────────────────────────────────────
 *  A "WhatsApp Web"-style screen INSIDE the portal: see every WhatsApp
 *  conversation the caller may see + reply by typing. TWO-WAY.
 *
 *  Visibility (owner-locked, conversation-visibility model):
 *   - Admin   → ALL conversations across both firms.
 *   - Staff   → ONLY conversations on projects they are assigned to.
 *   - Client  → NEVER reaches this screen (the route is admin/staff-gated
 *               in App.tsx AND this component re-asserts the track AND the
 *               server callable rejects a client token). Defence in depth.
 *
 *  The 24-hour window is shown per conversation: open → free-text box;
 *  closed → the box is replaced by an approved-template picker. The user
 *  never blindly sends into a closed window.
 *
 *  Sends go through waSendManualReply — ONE explicit reply per click. No
 *  bulk, no broadcast, no auto-send.
 *
 *  Data shapes / colours are byte-faithful to the signed-off mockup.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Box, EmptyNote } from '../components/ui';
import {
  applyFilter,
  attachAvailability,
  fetchConversations,
  filterCounts,
  fmtClock,
  isBotRow,
  markConversationRead,
  PRIMARY_PHONE_NUMBER_ID,
  receivingFilterOptions,
  replyFromPhone,
  sendManualReply,
  setConversationAiPaused,
  sortConversations,
  uploadMessageAttachment,
  windowLabel,
  type ConversationRow,
  type ConvFilter,
  type ThreadView,
} from '../lib/messagesStore';
import {
  formatReceivingPhoneBadge,
  RECEIVING_NUMBER_ROLES,
} from '../lib/receivingPhone';
import {
  listenTriageQueue,
  listenArchivedQueue,
  createLeadFromTriage,
  createContactFromTriage,
  setTriageAction,
  type DraftContact,
} from '../lib/triageStore';

function initialsOf(name: string): string {
  return (
    name
      .replace(/\+/g, '')
      .split(/[\s_]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join('') || '#'
  );
}

export default function Messages() {
  const { role, user } = useAuth();

  const [rows, setRows] = useState<ConversationRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Pagination over the (now index-backed) conversation list. The server
  // returns the most-recent page + a cursor; "Load older" pages deeper
  // WITHOUT any project scan.
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [filter, setFilter] = useState<ConvFilter>('all');
  const [search, setSearch] = useState('');
  // ITEM 2 — per-number filter (which OF OUR numbers received the chat).
  // INDEPENDENT axis from the segmented filter above. 'all' or a Meta
  // phone-number-id. Labels derive from receivingPhone.ts (never hardcoded).
  const [receivingFilter, setReceivingFilter] = useState<string>('all');

  // ITEM 1 — the OPEN conversation is persisted in the URL (`?c=<key>`), so a
  // full BROWSER reload restores it (it is shareable + bookmarkable). The
  // route itself (/messages) is a real path served by the SPA rewrite; the
  // earlier "reload drops me to Home" bug was the auth role-gate redirecting
  // before claims resolved (fixed in RequireAuth) — this makes the OPEN chat
  // survive too. activeKey is derived from the search param so the two never
  // drift; setActiveKey writes the param.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeKey = searchParams.get('c');
  const setActiveKey = useCallback(
    (key: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (key) next.set('c', key);
          else next.delete('c');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [thread, setThread] = useState<ThreadView | null>(null);
  const [threadLoading, setThreadLoading] = useState(false);

  // LIVE RECEIVE (item 1). The WhatsApp pipeline collections
  // (wa_conversations / wa_inbound / wa_agent_threads / draft_contacts) are
  // SERVER-ONLY by firestore.rules (default-deny) — the portal can NEVER open
  // a client-side onSnapshot on them; visibility spans both firms and is
  // enforced inside waListConversations. So "instant receive" is implemented
  // as a short-interval POLL of that same callable while this screen is
  // mounted: the list AND the open thread re-fetch on a timer, so a new
  // inbound appears without any manual refresh. The poll is silent (no
  // spinners), guards against overlapping in-flight calls, and is torn down
  // on unmount / route change (the cleanup clears the timer). This is the
  // correct path given the locked server-only model — opening the index to
  // direct client reads would be a security regression.
  const LIVE_POLL_MS = 12_000;
  // Mirrors of the latest state for the interval callback (so the timer body
  // reads fresh values without re-creating the interval on every change).
  const activeKeyRef = useRef<string | null>(null);
  const pollingRef = useRef(false);
  useEffect(() => {
    activeKeyRef.current = activeKey;
  }, [activeKey]);

  const [draft, setDraft] = useState('');
  const [tpl, setTpl] = useState('');
  const [tplParams, setTplParams] = useState('');
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);
  // ITEM 5 — "Take over / Resume AI" in flight (disables the toggle button).
  const [aiToggling, setAiToggling] = useState(false);

  // Attach (ISSUE 2). One explicit, user-initiated media send per pick —
  // no bulk/auto. The hidden input is triggered by the 📎 button.
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [attaching, setAttaching] = useState(false);

  // ── Triage Queue state (M3.1 2026-06-18, AD-015) ──────────────────────
  const [activeTab, setActiveTab] = useState<'inbox' | 'triage'>('inbox');
  const [triageItems, setTriageItems] = useState<DraftContact[]>([]);
  const [archivedItems, setArchivedItems] = useState<DraftContact[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [expandedTriage, setExpandedTriage] = useState<string | null>(null);
  const [triageLoading, setTriageLoading] = useState(false);
  const [contactForm, setContactForm] = useState<{draftId: string; phone: string} | null>(null);
  const [contactFormName, setContactFormName] = useState('');
  const [contactFormType, setContactFormType] = useState('Vendor');

  useEffect(() => {
    const unsub = listenTriageQueue(setTriageItems);
    return unsub;
  }, []);

  useEffect(() => {
    if (!showArchived) return;
    const unsub = listenArchivedQueue(setArchivedItems);
    return unsub;
  }, [showArchived]);

  // FIXED-VIEWPORT lock (regression class #945/#946). The Messages
  // workspace is a WhatsApp-Web-style console: the whole page must NOT
  // scroll — only the inner panes. messages.css scopes the viewport
  // lock to `body.msgs-screen`; we add it on mount and ALWAYS remove it
  // on unmount so every other route keeps normal page scroll. Scoped to
  // this component = zero blast radius on the rest of the portal.
  useEffect(() => {
    document.body.classList.add('msgs-screen');
    return () => {
      document.body.classList.remove('msgs-screen');
    };
  }, []);

  // `silent` (poll-driven) refresh: never flips the list back to the
  // loading state and never surfaces a transient network blip as an error —
  // it just swaps in the newest page if it succeeds.
  const loadList = useCallback(async (silent = false) => {
    if (!silent) setErr(null);
    try {
      const res = await fetchConversations();
      setRows(sortConversations(res.conversations));
      setNextCursor(res.nextCursor ?? null);
    } catch {
      if (!silent) {
        setErr('Could not load conversations. Try again.');
        setRows([]);
        setNextCursor(null);
      }
      // A silent poll failure is swallowed — the existing list stays put.
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (nextCursor == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetchConversations(undefined, { cursor: nextCursor });
      setRows((prev) => {
        const seen = new Set((prev || []).map((r) => r.key));
        const merged = [
          ...(prev || []),
          ...res.conversations.filter((r) => !seen.has(r.key)),
        ];
        return sortConversations(merged);
      });
      setNextCursor(res.nextCursor ?? null);
    } catch {
      setErr('Could not load older conversations. Try again.');
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // Load ONE conversation's thread + clear its unread badge. Pulled out of
  // the click handler so the URL-restore effect (item 1) can reuse it: a
  // browser reload on `/messages?c=<key>` lands here with the key already set
  // from the URL and loads the thread without a click.
  const loadThread = useCallback(async (key: string) => {
    setThread(null);
    setThreadLoading(true);
    setSendErr(null);

    // ITEM 2/3 — opening = reading. Clear the unread badge. The badge is
    // server-only `wa_conversations.unreadByTeam`; the portal can't write
    // it, so we (a) OPTIMISTICALLY zero this row locally for an instant
    // badge clear, then (b) tell the server to persist 0 so the next poll
    // (and any other open session) sees it cleared too. Best-effort — a
    // mark-read failure must never block reading the conversation.
    setRows((prev) =>
      prev
        ? prev.map((r) => (r.key === key ? { ...r, unread: 0 } : r))
        : prev,
    );
    void markConversationRead(key).catch(() => {
      /* non-fatal: optimistic clear stands; next poll re-reconciles */
    });

    try {
      const res = await fetchConversations(key);
      setThread(res.thread || null);
    } catch {
      setSendErr('Could not open that conversation.');
    } finally {
      setThreadLoading(false);
    }
  }, []);

  // User clicked a conversation row: persist it in the URL (so reload
  // restores it) + reset the composer. The thread load happens in the
  // activeKey effect below (one place, shared with reload-restore).
  const openConversation = useCallback(
    (key: string) => {
      setDraft('');
      setTpl('');
      setTplParams('');
      setActiveKey(key);
    },
    [setActiveKey],
  );

  // ITEM 1 — load the thread whenever the OPEN key changes. This fires on a
  // row click AND on a browser reload that restored `?c=<key>` from the URL,
  // so the open conversation survives a hard refresh. Guarded against a
  // missing key (list view) — then we just clear the thread.
  useEffect(() => {
    if (!activeKey) {
      setThread(null);
      return;
    }
    void loadThread(activeKey);
  }, [activeKey, loadThread]);

  // Silent (poll-driven) re-fetch of the currently OPEN thread — no spinner,
  // swallows transient errors, only replaces the thread on success so a new
  // inbound message appears live without a manual refresh. Re-marks read so
  // a message that arrives while the thread is open clears immediately.
  const refreshOpenThread = useCallback(async (key: string) => {
    try {
      const res = await fetchConversations(key);
      if (res.thread) setThread(res.thread);
      setRows((prev) =>
        prev
          ? prev.map((r) => (r.key === key ? { ...r, unread: 0 } : r))
          : prev,
      );
      void markConversationRead(key).catch(() => {});
    } catch {
      /* silent — keep the current thread */
    }
  }, []);

  // LIVE POLL (item 1) — while this screen is mounted, refresh the list and
  // (if one is open) the active thread on a timer so new inbound messages
  // appear without a manual refresh. ONE interval for the component's whole
  // lifetime; the body reads the latest activeKey via a ref so we never
  // re-create the timer. `pollingRef` collapses overlapping ticks if a fetch
  // is slow. Torn down on unmount / route change (cleanup clears the timer).
  useEffect(() => {
    const id = window.setInterval(() => {
      if (pollingRef.current) return; // a prior tick is still in flight
      // Pause polling when the tab is hidden — no point burning callable
      // invocations while the owner is on another tab; the visibility-change
      // listener below does an immediate catch-up refresh on return.
      if (typeof document !== 'undefined' && document.hidden) return;
      pollingRef.current = true;
      void (async () => {
        try {
          await loadList(true);
          const k = activeKeyRef.current;
          if (k) await refreshOpenThread(k);
        } finally {
          pollingRef.current = false;
        }
      })();
    }, LIVE_POLL_MS);

    // Catch up immediately when the tab regains focus (the owner switches
    // back to the portal and expects to see anything that arrived).
    function onVisible() {
      if (typeof document !== 'undefined' && document.hidden) return;
      void loadList(true);
      const k = activeKeyRef.current;
      if (k) void refreshOpenThread(k);
    }
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadList, refreshOpenThread]);

  // Defence-in-depth: admin/staff track only (route is also role-gated;
  // client token is rejected by the callable). Clients never see this.
  if (role !== 'admin' && role !== 'staff') {
    return (
      <Box title="WhatsApp Inbox" note="Restricted">
        <EmptyNote title="Not available on this track">
          The WhatsApp workspace is a team operations tool.
        </EmptyNote>
      </Box>
    );
  }

  async function doSend() {
    if (!activeKey || !thread) return;
    setSending(true);
    setSendErr(null);
    try {
      if (thread.window.open) {
        if (!draft.trim()) {
          setSendErr('Type a message first.');
          return;
        }
        await sendManualReply({ conversationKey: activeKey, text: draft });
      } else {
        if (!tpl) {
          setSendErr('Choose an approved template.');
          return;
        }
        await sendManualReply({
          conversationKey: activeKey,
          templateName: tpl,
          templateParams: tplParams
            .split('|')
            .map((s) => s.trim())
            .filter(Boolean),
        });
      }
      // Refresh the open thread + list so the sent reply shows immediately.
      await loadThread(activeKey);
      await loadList();
    } catch (e) {
      const msg =
        (e as { message?: string }).message || 'Send failed. Try again.';
      setSendErr(
        /send_blocked/.test(msg)
          ? 'WhatsApp blocked that send (window closed or template not approved).'
          : 'Send failed — the message did not go out. Try again.',
      );
    } finally {
      setSending(false);
    }
  }

  // ITEM 5 — Take over (pause AI) / Resume AI for the open conversation. The
  // flag lives on the server-only conv-index; we OPTIMISTICALLY flip the row
  // for instant UI feedback, call the callable, then reconcile (and revert on
  // failure — the toggle must reflect the true server state). When paused,
  // the inbound auto-reply path skips its AI reply and the human replies via
  // the normal composer below (same dual-number send path).
  async function toggleAiPaused() {
    if (!activeKey) return;
    const next = !aiPaused;
    setAiToggling(true);
    setSendErr(null);
    setRows((prev) =>
      prev
        ? prev.map((r) => (r.key === activeKey ? { ...r, aiPaused: next } : r))
        : prev,
    );
    try {
      const res = await setConversationAiPaused(activeKey, next);
      // Reconcile to the authoritative server value.
      setRows((prev) =>
        prev
          ? prev.map((r) =>
              r.key === activeKey ? { ...r, aiPaused: res.aiPaused } : r,
            )
          : prev,
      );
    } catch {
      // Revert the optimistic flip — the change did not take.
      setRows((prev) =>
        prev
          ? prev.map((r) =>
              r.key === activeKey ? { ...r, aiPaused: !next } : r,
            )
          : prev,
      );
      setSendErr(
        next
          ? 'Could not take over — the AI may still auto-reply. Try again.'
          : 'Could not resume the AI. Try again.',
      );
    } finally {
      setAiToggling(false);
    }
  }

  // ── Triage action handlers (M3.1 2026-06-18, AD-015) ─────────────────
  async function handleTriageCreateLead(draft: DraftContact) {
    if (!user) return;
    setTriageLoading(true);
    try {
      await createLeadFromTriage(draft, user.uid);
    } catch (e) {
      console.error('Triage create lead failed', e);
    } finally {
      setTriageLoading(false);
    }
  }

  function handleTriageCreateContact(draft: DraftContact) {
    setContactForm({ draftId: draft.id, phone: draft.phone });
    setContactFormName('');
    setContactFormType('Vendor');
  }

  async function handleTriageContactFormSubmit(draft: DraftContact) {
    if (!user || !contactFormName.trim()) return;
    setTriageLoading(true);
    try {
      await createContactFromTriage(draft, contactFormName.trim(), contactFormType, user.uid);
      setContactForm(null);
    } catch (e) {
      console.error('Triage create contact failed', e);
    } finally {
      setTriageLoading(false);
    }
  }

  async function handleTriageArchive(draft: DraftContact) {
    if (!user) return;
    await setTriageAction(draft.id, 'archive', user.uid);
  }

  async function handleTriageHold(draft: DraftContact) {
    if (!user) return;
    await setTriageAction(draft.id, 'hold', user.uid);
  }

  const TRIAGE_LABEL_STYLE: Record<string, React.CSSProperties> = {
    probable_lead: { background: '#dbeafe', color: '#1e40af' },
    probable_vendor: { background: '#fef9c3', color: '#854d0e' },
    probable_service_auto: { background: '#f3f4f6', color: '#374151' },
    unknown_intent: { background: '#f3f4f6', color: '#374151' },
  };
  const TRIAGE_LABEL_TEXT: Record<string, string> = {
    probable_lead: 'Probable Lead',
    probable_vendor: 'Probable Vendor',
    probable_service_auto: 'Auto/Service',
    unknown_intent: 'Unknown Intent',
  };

  const list = rows ? applyFilter(rows, filter, search, receivingFilter) : [];
  const counts = rows
    ? filterCounts(rows)
    : { all: 0, unread: 0, triage: 0, mine: 0, customers: 0, bots: 0 };
  // ITEM 2 — per-number "Showing:" dropdown options. Role labels (Sales /
  // Client Care) are config-driven in receivingPhone.ts; the digit sub-line
  // derives from the SAME badge model the row chips use (never hardcoded
  // twice). A dropdown (not a chip-row) so it stays tappable on a narrow
  // phone — the portal ships inside the Capacitor app.
  const numberOptions = rows
    ? receivingFilterOptions(
        rows,
        RECEIVING_NUMBER_ROLES,
        formatReceivingPhoneBadge,
      )
    : [{ id: 'all', label: 'All numbers', line: '', count: 0 }];
  const active = rows?.find((r) => r.key === activeKey) || null;
  // ITEM 3 — which OF OUR numbers a reply on the open conversation sends
  // from (server dual-number resolver mirror: same number the last inbound
  // landed on; legacy/unknown → PRIMARY). Honest composer label.
  const replyFrom = replyFromPhone(
    active?.receivingPhoneNumberId,
    formatReceivingPhoneBadge,
    PRIMARY_PHONE_NUMBER_ID,
  );
  // ITEM 5 — pause state of the open conversation (AI replies by default).
  const aiPaused = !!active?.aiPaused;

  // ONE explicit, user-initiated media send per file pick. Upload to
  // Storage (reuses the shipped project-files path) then send via the
  // SAME single-message callable. Server re-gates the 24h window + the
  // visibility + records it (thread + comms + audit). No bulk/auto.
  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Always clear the input so picking the same file again re-fires.
    if (e.target) e.target.value = '';
    if (!file || !activeKey || !active || !thread) return;

    const gate = attachAvailability(active, thread.window);
    if (!gate.ok) {
      setSendErr(gate.reason);
      return;
    }
    setAttaching(true);
    setSendErr(null);
    try {
      const attachment = await uploadMessageAttachment(
        (active.projectId || 'triage') as string,
        file,
        {
          uid: user?.uid || 'unknown',
          name: user?.displayName || user?.email || 'Team',
        },
      );
      await sendManualReply({
        conversationKey: activeKey,
        text: draft.trim() || undefined,
        attachment,
      });
      setDraft('');
      await loadThread(activeKey);
      await loadList();
    } catch (err) {
      const msg =
        (err as { message?: string }).message || 'Attach failed. Try again.';
      setSendErr(
        /send_blocked:window_closed_media_blocked/.test(msg)
          ? 'The 24-hour window closed while sending — a file cannot be ' +
            'sent outside it. Try again once the client messages.'
          : /not an accepted file type|the limit is/.test(msg)
            ? msg // friendly client-side validation message from projectFiles
            : 'The file did not go out. It was uploaded but not delivered ' +
              'as a WhatsApp message — try again.',
      );
    } finally {
      setAttaching(false);
    }
  }

  return (
    <div className={`wa-wrap${activeKey ? ' has-active' : ''}`}>
      {/* ── Tab bar (Inbox / Triage) M3.1 2026-06-18 ── */}
      <div style={{display:'flex',borderBottom:'1px solid #e5e7eb',background:'#fff',padding:'0 16px',flexShrink:0}}>
        <button
          onClick={() => setActiveTab('inbox')}
          style={{padding:'10px 16px',border:'none',background:'none',cursor:'pointer',fontSize:'13px',fontWeight: activeTab==='inbox' ? 'bold' : 'normal',borderBottom: activeTab==='inbox' ? '2px solid #111' : '2px solid transparent',color: activeTab==='inbox' ? '#111' : '#555'}}
        >
          Inbox
        </button>
        <button
          onClick={() => setActiveTab('triage')}
          style={{padding:'10px 16px',border:'none',background:'none',cursor:'pointer',fontSize:'13px',fontWeight: activeTab==='triage' ? 'bold' : 'normal',borderBottom: activeTab==='triage' ? '2px solid #111' : '2px solid transparent',color: activeTab==='triage' ? '#111' : '#555'}}
        >
          Triage {triageItems.length > 0 ? `(${triageItems.length})` : ''}
        </button>
      </div>

      {err && activeTab === 'inbox' && (
        <div className="wa-banner" role="alert">
          {err}
        </div>
      )}

      {/* ── Triage pane ── */}
      {activeTab === 'triage' && (
        <div style={{display:'flex',flexDirection:'column',height:'calc(100% - 41px)',overflow:'auto',padding:'16px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'12px'}}>
            <span style={{fontSize:'13px',color:'#555'}}>
              {archivedItems.length > 0 && showArchived ? `${archivedItems.length} archived shown` : ''}
            </span>
            <button
              onClick={() => setShowArchived(v => !v)}
              style={{fontSize:'11px',padding:'3px 10px',borderRadius:'4px',border:'1px solid #ddd',background:'#f9f9f9',cursor:'pointer'}}
            >
              {showArchived ? 'Hide Archived' : 'Show Archived'}
            </button>
          </div>

          {showArchived && archivedItems.map(item => (
            <div key={item.id} style={{border:'1px solid #e5e7eb',borderRadius:'6px',padding:'12px',marginBottom:'8px',background:'#fafafa'}}>
              <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px'}}>
                <span style={{fontWeight:'bold',fontSize:'13px'}}>{item.phone}</span>
                <span style={{fontSize:'11px',padding:'1px 7px',borderRadius:'10px',fontWeight:'bold',...(TRIAGE_LABEL_STYLE[item.aiLabel] || TRIAGE_LABEL_STYLE.unknown_intent)}}>
                  {TRIAGE_LABEL_TEXT[item.aiLabel] || 'Unknown'}
                </span>
                <span style={{fontSize:'11px',color:'#888',marginLeft:'auto'}}>Auto-archived: {item.autoArchivedReason}</span>
              </div>
              <p style={{fontSize:'12px',color:'#555',margin:0}}>{item.firstMessage}</p>
            </div>
          ))}

          {triageItems.length === 0 && !showArchived && (
            <div style={{textAlign:'center',color:'#888',padding:'40px'}}>No items in triage queue</div>
          )}

          {triageItems.map(item => (
            <div key={item.id} style={{border:'1px solid #e5e7eb',borderRadius:'6px',padding:'12px',marginBottom:'8px',background:'#fff'}}>
              <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px'}}>
                <span style={{fontWeight:'bold',fontSize:'13px'}}>{item.phone}</span>
                <span style={{fontSize:'11px',padding:'1px 7px',borderRadius:'10px',fontWeight:'bold',...(TRIAGE_LABEL_STYLE[item.aiLabel] || TRIAGE_LABEL_STYLE.unknown_intent)}}>
                  {TRIAGE_LABEL_TEXT[item.aiLabel] || 'Unknown'}
                </span>
                <span style={{fontSize:'11px',color:'#888',marginLeft:'auto'}}>
                  {item.messageCount ? `${item.messageCount} msg` : ''}
                </span>
              </div>

              <p style={{fontSize:'12px',color:'#333',margin:'0 0 6px',cursor:'pointer'}} onClick={() => setExpandedTriage(expandedTriage === item.id ? null : item.id)}>
                {expandedTriage === item.id ? item.firstMessage : (item.firstMessage || '').slice(0, 80) + ((item.firstMessage || '').length > 80 ? '…' : '')}
              </p>

              {contactForm?.draftId === item.id && (
                <div style={{background:'#f9f9f9',border:'1px solid #ddd',borderRadius:'4px',padding:'10px',marginBottom:'8px'}}>
                  <div style={{display:'flex',gap:'8px',marginBottom:'6px'}}>
                    <input
                      type="text"
                      placeholder="Name"
                      value={contactFormName}
                      onChange={e => setContactFormName(e.target.value)}
                      style={{flex:1,padding:'4px 8px',border:'1px solid #ccc',borderRadius:'4px',fontSize:'12px'}}
                    />
                    <select
                      value={contactFormType}
                      onChange={e => setContactFormType(e.target.value)}
                      style={{padding:'4px 8px',border:'1px solid #ccc',borderRadius:'4px',fontSize:'12px'}}
                    >
                      <option>Vendor</option>
                      <option>Colleague</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div style={{display:'flex',gap:'6px'}}>
                    <button onClick={() => void handleTriageContactFormSubmit(item)} disabled={!contactFormName.trim()} style={{fontSize:'12px',padding:'4px 10px',borderRadius:'4px',background:'#111',color:'#fff',border:'none',cursor:'pointer'}}>
                      Save Contact
                    </button>
                    <button onClick={() => setContactForm(null)} style={{fontSize:'12px',padding:'4px 10px',borderRadius:'4px',background:'#f3f4f6',border:'1px solid #ddd',cursor:'pointer'}}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                <button onClick={() => void handleTriageCreateLead(item)} disabled={triageLoading} style={{fontSize:'12px',padding:'4px 10px',borderRadius:'4px',background:'#dbeafe',color:'#1e40af',border:'1px solid #bfdbfe',cursor:'pointer',fontWeight:'bold'}}>
                  Create Lead
                </button>
                <button onClick={() => handleTriageCreateContact(item)} disabled={triageLoading} style={{fontSize:'12px',padding:'4px 10px',borderRadius:'4px',background:'#f0fdf4',color:'#166534',border:'1px solid #bbf7d0',cursor:'pointer',fontWeight:'bold'}}>
                  Create Contact
                </button>
                <button onClick={() => void handleTriageArchive(item)} disabled={triageLoading} style={{fontSize:'12px',padding:'4px 10px',borderRadius:'4px',background:'#f9fafb',color:'#374151',border:'1px solid #e5e7eb',cursor:'pointer'}}>
                  Archive
                </button>
                <button onClick={() => void handleTriageHold(item)} disabled={triageLoading} style={{fontSize:'12px',padding:'4px 10px',borderRadius:'4px',background:'#fef9c3',color:'#854d0e',border:'1px solid #fde68a',cursor:'pointer'}}>
                  Hold
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'inbox' && <div className="wa">
        {/* ─────────── conversation list ─────────── */}
        <div className="wa-list">
          <div className="wa-list-top">
            <div className="wa-search">
              <span aria-hidden>⌕</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or number…"
                aria-label="Search conversations"
              />
            </div>
            <div className="wa-filters" role="tablist">
              {(
                [
                  ['all', 'All', counts.all],
                  ['unread', 'Unread', counts.unread],
                  ['triage', 'Triage', counts.triage],
                  ['mine', 'My projects', counts.mine],
                  // ITEM 4 — separate real customer chats from the AI-bot /
                  // internal (agent:) threads that auto-reply.
                  ['customers', 'Customers', counts.customers],
                  ['bots', 'AI / internal', counts.bots],
                ] as Array<[ConvFilter, string, number]>
              ).map(([k, lbl, n]) => (
                <button
                  key={k}
                  className={`chip${filter === k ? ' on' : ''}`}
                  aria-pressed={filter === k}
                  onClick={() => setFilter(k)}
                >
                  {lbl}
                  {n > 0 && <span className="n">{n}</span>}
                </button>
              ))}
            </div>
            {/* ITEM 2 — per-number filter as a compact "Showing:" DROPDOWN
                (replaces the earlier chip-row: a dropdown stays tappable on a
                narrow phone, and the portal ships inside the Capacitor app).
                INDEPENDENT axis from the segmented filter above — combines
                with the active tab. Role labels (Sales / Client Care) are
                config-driven in receivingPhone.ts; the digit lines derive from
                the same badge model — never hardcoded here. Shown only when
                there is a real choice (more than one number). */}
            {numberOptions.length > 2 && (
              <div className="wa-numbersel">
                <label htmlFor="wa-number-filter">Showing:</label>
                <select
                  id="wa-number-filter"
                  value={receivingFilter}
                  onChange={(e) => setReceivingFilter(e.target.value)}
                  aria-label="Filter conversations by number"
                >
                  {numberOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.id === 'all'
                        ? `All numbers${opt.count > 0 ? ` (${opt.count})` : ''}`
                        : `${opt.label}${opt.line ? ` · ${opt.line}` : ''}${
                            opt.count > 0 ? ` (${opt.count})` : ''
                          }`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="wa-threads">
            {rows === null && (
              <div className="wa-empty">Loading conversations…</div>
            )}
            {rows !== null && list.length === 0 && (
              <div className="wa-empty">No conversations match.</div>
            )}
            {list.map((r) => {
              // Multi-sender badge — which OF OUR numbers received the
              // last inbound. Null-safe: legacy rows (pre-8319bb1) have
              // no field; the helper returns null and we render no chip.
              const recv = formatReceivingPhoneBadge(r.receivingPhoneNumberId);
              // ITEM 4 — AI-bot / internal (agent:) thread vs a real customer.
              const bot = isBotRow(r);
              return (
                <button
                  key={r.key}
                  className={`thr${r.key === activeKey ? ' on' : ''}${
                    bot ? ' bot' : ''
                  }`}
                  onClick={() => openConversation(r.key)}
                >
                  <span
                    className={`av${r.triage ? ' unk' : ''}${bot ? ' bot' : ''}`}
                    aria-hidden
                  >
                    {bot ? '🤖' : r.triage ? '+91' : initialsOf(r.name)}
                  </span>
                  <span className="thr-main">
                    <span className="thr-top">
                      <span className="thr-name">{r.name}</span>
                      <span className="thr-time">
                        {fmtClock(r.lastMessageAt)}
                      </span>
                    </span>
                    <span className="thr-snip">{r.lastMessage || '—'}</span>
                    <span className="thr-meta">
                      {/* ITEM 4 — a clear, unmissable tag on the bot/internal
                          rows so the team never mistakes them for customers. */}
                      {bot && (
                        <span className="tag bot">AI bot · internal</span>
                      )}
                      {/* ITEM 5 — human has taken over (AI auto-reply paused). */}
                      {r.aiPaused && (
                        <span className="tag human">Human replying</span>
                      )}
                      {r.projectRef && (
                        <span className="tag proj">{r.projectRef}</span>
                      )}
                      {!bot && r.aiHandled && (
                        <span className="tag ai">AI replied</span>
                      )}
                      {r.triage && (
                        <span className="tag triage">
                          Unknown · triage lead
                        </span>
                      )}
                    </span>
                  </span>
                  {r.unread > 0 && (
                    <span className="unread">{r.unread}</span>
                  )}
                  {recv && (
                    <span
                      className={`recv-badge ${recv.classKey}`}
                      title={`Received via ${recv.fullPhone}`}
                      aria-label={`Received via ${recv.fullPhone}`}
                    >
                      → {recv.label}
                    </span>
                  )}
                </button>
              );
            })}
            {rows !== null && nextCursor != null && (
              <button
                type="button"
                className="wa-load-more"
                onClick={() => void loadMore()}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load older conversations'}
              </button>
            )}
          </div>
        </div>

        {/* ─────────── conversation pane ─────────── */}
        <div className="wa-conv">
          {!activeKey && (
            <div className="wa-conv-empty">
              <EmptyNote title="Select a conversation">
                Pick a chat on the left to read the full history and reply.
              </EmptyNote>
            </div>
          )}

          {activeKey && (
            <>
              <div className="conv-head">
                <button
                  className="back"
                  aria-label="Back to list"
                  onClick={() => {
                    setActiveKey(null);
                    setThread(null);
                  }}
                >
                  ‹
                </button>
                <span className="av" aria-hidden>
                  {initialsOf(active?.name || '#')}
                </span>
                <span className="who">
                  <b>{active?.name || 'Conversation'}</b>
                  <span className="sub">
                    {active?.phone || ''}
                    {active?.projectRef ? ` · ${active.projectRef}` : ''}
                  </span>
                  {(() => {
                    // Multi-sender attribution on the chat header.
                    // Prefer the active conversation row's
                    // receivingPhoneNumberId (the LATEST inbound). Fall
                    // back to the latest inbound message's field for
                    // older index docs that haven't been re-written yet.
                    const lastInbound = thread?.messages
                      ?.slice()
                      .reverse()
                      .find((m) => m.direction === 'in');
                    const recv = formatReceivingPhoneBadge(
                      active?.receivingPhoneNumberId ||
                        lastInbound?.receivingPhoneNumberId,
                    );
                    if (!recv) return null;
                    return (
                      <span
                        className={`recv-badge head ${recv.classKey}`}
                        title={`Last inbound received via ${recv.fullPhone}`}
                        aria-label={`Last inbound received via ${recv.fullPhone}`}
                      >
                        → {recv.label}
                      </span>
                    );
                  })()}
                </span>
                {active?.projectId && (
                  <Link
                    className="lnk"
                    to={`/conversation/${active.projectId}`}
                  >
                    Open project ↗
                  </Link>
                )}
                {/* ITEM 5 — Take over (pause AI) / Resume AI. The AI replies
                    by default; tapping "Take over" pauses the auto-reply so
                    the human handles this chat. Shown for every conversation
                    (the gate is server-side per-conversation). */}
                <button
                  type="button"
                  className={`ai-toggle${aiPaused ? ' paused' : ''}`}
                  disabled={aiToggling}
                  aria-pressed={aiPaused}
                  title={
                    aiPaused
                      ? 'AI is paused — you are replying manually. Tap to let the AI auto-reply again.'
                      : 'The AI auto-replies to this conversation. Tap to take over and reply manually.'
                  }
                  onClick={() => void toggleAiPaused()}
                >
                  {aiToggling
                    ? '…'
                    : aiPaused
                      ? '▶ Resume AI'
                      : '⏸ Take over'}
                </button>
              </div>

              {/* ITEM 5 — paused state banner (clear, unmissable). */}
              {aiPaused && (
                <div className="conv-paused" role="status">
                  AI paused — you’re replying manually. Resume AI to let the
                  assistant auto-reply again.
                </div>
              )}

              <div className="conv-body">
                {threadLoading && (
                  <div className="wa-empty">Loading messages…</div>
                )}
                {!threadLoading &&
                  thread &&
                  thread.messages.length === 0 && (
                    <div className="wa-empty">No messages yet.</div>
                  )}
                {!threadLoading &&
                  thread &&
                  thread.messages.map((m, i) => (
                    <div
                      key={m.id || i}
                      className={`msg ${m.direction === 'in' ? 'in' : 'out'}${
                        m.who === 'ai' ? ' ai' : ''
                      }`}
                    >
                      {m.direction === 'out' && (
                        <span
                          className={`who-badge ${
                            m.who === 'ai' ? 'ai' : 'human'
                          }`}
                        >
                          {m.who === 'ai'
                            ? 'AI · auto-reply'
                            : 'Sent manually'}
                        </span>
                      )}
                      {m.body !== '' ? (
                        m.body
                      ) : !m.attachment ? (
                        // Honest empty-body chip. Meta sometimes delivers
                        // a message with NO content payload (msgType
                        // 'unsupported', error 131051) — the verbatim body
                        // is genuinely '' end-to-end. Do NOT synthesise a
                        // fake string (verbatim-sacrosanct,
                        // feedback_client_verbatim_preservation). Surface
                        // the truth instead.
                        <span className="msg-empty">
                          WhatsApp couldn’t deliver this message’s content —
                          the sender used a type the WhatsApp Business API
                          can’t receive (e.g. a view-once photo, poll, or
                          certain stickers). Ask them to resend it as a normal
                          photo or message.
                        </span>
                      ) : null}
                      {m.attachment && m.attachment.downloadURL && (
                        <a
                          className="msg-att"
                          href={m.attachment.downloadURL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {m.attachment.kind === 'image' ? '🖼' : '📎'}{' '}
                          {m.attachment.fileName || 'attachment'}
                        </a>
                      )}
                      <span className="t">
                        {fmtClock(m.at)}
                        {m.deliveryState && (
                          <span className="tick">
                            {' '}
                            ✓✓ {m.deliveryState}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
              </div>

              {thread && (
                <>
                  <div
                    className={`winbar ${
                      thread.window.open ? 'open' : 'closed'
                    }`}
                  >
                    <span className="dot" aria-hidden />
                    {windowLabel(thread.window)}
                  </div>

                  {sendErr && (
                    <div className="wa-send-err" role="alert">
                      {sendErr}
                    </div>
                  )}

                  {/* ITEM 3 — the ACTUAL number this reply sends from for the
                      OPEN conversation (server dual-number resolver mirror:
                      same number the last inbound landed on; legacy/unknown →
                      PRIMARY). Replaces the old vague "the Aayatan Veda
                      WhatsApp number" copy. */}
                  <div className="reply-from" aria-live="polite">
                    Replying from <b>{replyFrom}</b>
                  </div>

                  {thread.window.open ? (
                    <div className="composer" id="cs-composer">
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                        style={{ display: 'none' }}
                        onChange={(e) => void onPickFile(e)}
                        aria-hidden
                      />
                      <button
                        type="button"
                        className="cs-attach"
                        id="cs-attach"
                        title={
                          attachAvailability(active, thread.window).ok
                            ? 'Attach a file or document'
                            : 'Attachments unavailable for this conversation'
                        }
                        aria-label="Attach a file or document"
                        disabled={
                          sending ||
                          attaching ||
                          !attachAvailability(active, thread.window).ok
                        }
                        onClick={() => fileRef.current?.click()}
                      >
                        📎
                      </button>
                      <textarea
                        id="cs-ti"
                        className="msg-ti"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder={
                          attaching
                            ? 'Sending attachment…'
                            : `Type a reply… (sends from ${replyFrom})`
                        }
                        aria-label="Reply message"
                        disabled={attaching}
                      />
                      <button
                        className="send"
                        id="cs-send"
                        disabled={sending || attaching || !draft.trim()}
                        onClick={() => void doSend()}
                      >
                        {sending ? 'Sending…' : 'Send ▸'}
                      </button>
                    </div>
                  ) : (
                    <div
                      className="composer closed-window-notice"
                      style={{
                        padding: '12px 16px',
                        background: '#f9fafb',
                        borderTop: '1px solid #e5e7eb',
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          color: '#6b7280',
                          fontSize: '13px',
                          lineHeight: '1.5',
                        }}
                      >
                        <strong style={{ color: '#374151' }}>
                          24-hour window closed.
                        </strong>{' '}
                        The client needs to send a new message to reopen it.
                        Free typing will be available again once they do.
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>}
    </div>
  );
}
